import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { Group } from "@dashboard/devices/types/index";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { getRootGroupIdsInSelection } from "@dashboard/groups/utils";
import { Paper, TableContainer, TableRow, Theme, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import semverService from "@services/semverService/semverService";
import { CustomTileContainer, CustomTileTitle, HeaderContainer } from "@shared/CustomTile";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";

import DevicesTableItem from "./TableItem";

const useStyles = makeStyles()((muiTheme: Theme) => ({
    container: { height: "100%" },
    table: { marginTop: muiTheme.spacing(3), boxShadow: "none" },
}));

type OperatingSystemWithCount = {
    version: string;
    count: number;
};

const columnHelper = createColumnHelper<OperatingSystemWithCount>();

const getColumns = (theme: Theme) => {
    return [
        columnHelper.accessor("version", {
            id: "version",
            header: i18next.t("pages:overviewPage.table.header.version"),
            cell: (info) => (
                <MinimalTableCell style={{ borderBottom: `1px solid ${theme.palette.emteria.border.secondary}` }}>
                    <Typography>{info.getValue<string>()}</Typography>
                </MinimalTableCell>
            ),
        }),
        columnHelper.accessor("count", {
            id: "count",
            header: i18next.t("pages:overviewPage.table.header.devices"),
            cell: (info) => {
                const version = info.row.original.version;
                const count = info.getValue<number>();
                const filteredGroups = info.table.options.meta?.filteredGroupIds || [];
                return <DevicesTableItem count={count} version={version} selectedGroupIds={filteredGroups} />;
            },
            meta: {
                align: "right",
            },
        }),
    ];
};

const compareOsVersions = (a: OperatingSystemWithCount, b: OperatingSystemWithCount): number => {
    if (a.count !== b.count) {
        return b.count - a.count;
    }

    return semverService.compare(b.version, a.version);
};

const aggregateOsVersionCountsFromGroups = (groups: Group[], groupIds: number[]): OperatingSystemWithCount[] => {
    const targetGroupIds = groupIds.length > 0 ? groupIds : groups.map((g) => g.id);
    const rootGroupIds = getRootGroupIdsInSelection(targetGroupIds, groups);
    const targetGroups = groups.filter((g) => rootGroupIds.includes(g.id));

    if (targetGroups.length === 0 || targetGroups.some((g) => !g.statistics)) {
        return [];
    }

    const versionCounts: Record<string, number> = {};
    targetGroups.forEach((group) => {
        (group.statistics!.osVersions ?? []).forEach(({ version, count }) => {
            versionCounts[version] = (versionCounts[version] || 0) + count;
        });
    });

    return Object.entries(versionCounts)
        .map(([version, count]) => ({ version, count }))
        .sort(compareOsVersions);
};

type Props = {
    groupIds: number[];
};

export const OperatingSystemsCard = ({ groupIds }: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);
    const [sorting, setSorting] = useState<SortingState>([{ id: "count", desc: true }]);
    const theme = useTheme();

    const groups = useSelector(selectGroups);
    const filteredGroups = !groupIds.length ? groups.map((group) => group.id) : groupIds;

    const osVersionCounts = React.useMemo(
        () => aggregateOsVersionCountsFromGroups(groups, filteredGroups),
        [groups, filteredGroups],
    );

    const table = useReactTable<OperatingSystemWithCount>({
        data: osVersionCounts,
        columns: getColumns(theme),
        initialState: {
            pagination: {
                pageSize: 8,
            },
        },
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        meta: {
            filteredGroupIds: filteredGroups,
        },
    });

    const { pageSize, pageIndex } = table.getState().pagination;

    return (
        <CustomTileContainer className={classes.container}>
            <HeaderContainer>
                <CustomTileTitle>{t("pages:overviewPage.cards.operatingSystems.title")}</CustomTileTitle>
            </HeaderContainer>
            <TableContainer component={Paper} className={classes.table}>
                <MinimalTable stickyHeader>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header} />
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody<OperatingSystemWithCount> table={table} noDataMessage={t("common:table.noDevices")}>
                        {table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) =>
                                    flexRender(cell.column.columnDef.cell, {
                                        ...cell.getContext(),
                                        key: cell.id,
                                    }),
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </TableContainer>
            <CustomTablePagination
                component="div"
                rowsPerPageOptions={[8, 24, 48]}
                count={table.getFilteredRowModel().rows.length}
                rowsPerPage={pageSize}
                page={pageIndex}
                onPageChange={(_event, page) => table.setPageIndex(page)}
                onRowsPerPageChange={(e) => {
                    const size = e.target.value ? Number(e.target.value) : 8;
                    table.setPageSize(size);
                }}
            />
        </CustomTileContainer>
    );
};
